import axios from "axios";

const normalizeBase = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.endsWith("/") ? raw : `${raw}/`;
};

const DEFAULT_BASE = "https://hmam.prootech-cloud.com/api/";
const ENV_BASE = normalizeBase(process.env.REACT_APP_API_URL || DEFAULT_BASE);
const AXIOS_TIMEOUT_MS = 10000;

const getApiBaseCandidates = () => {
  const candidates = [
    ENV_BASE,
    DEFAULT_BASE,
    "http://127.0.0.1:5000/api/",
  ];

  if (typeof window !== "undefined") {
    if (window.location?.origin) {
      candidates.push(`${window.location.origin}/api/`);
    }
    if (window.location?.hostname && window.location?.protocol) {
      candidates.push(`${window.location.protocol}//${window.location.hostname}:5000/api/`);
    }
  }

  return [...new Set(candidates.map(normalizeBase).filter(Boolean))];
};

const requestWithFallback = async (factory) => {
  const bases = getApiBaseCandidates();
  let lastError = null;

  for (const base of bases) {
    try {
      return await factory(base);
    } catch (error) {
      lastError = error;
      // Fallback is only meaningful for connectivity issues.
      if (error?.response) {
        throw error;
      }
    }
  }

  throw lastError || new Error("Package API unavailable");
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const GetAllPackagesApi = async ({
  page = 1,
  limit = 10,
  filters = {},
}) => {
  const params = { page, limit };
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });

  try {
    const response = await requestWithFallback((base) =>
      axios.get(`${base}package/`, { params, timeout: AXIOS_TIMEOUT_MS })
    );

    const payload = response?.data || {};
    return {
      status: "success",
      data: Array.isArray(payload.data) ? payload.data : [],
      page: Number(payload.page) || 1,
      total: Number(payload.total) || 0,
      totalPages: Number(payload.totalPages) || 1,
      limit: Number(payload.limit) || Number(limit) || 10,
    };
  } catch (error) {
    console.error("[GetAllPackagesApi] Error:", error);
    return {
      status: "error",
      data: [],
      page: Number(page) || 1,
      total: 0,
      totalPages: 1,
      limit: Number(limit) || 10,
      error: error?.message || "Failed to fetch packages",
    };
  }
};

export const FetchCreatePackeges = async (formData) => {
  try {
    const response = await requestWithFallback((base) =>
      axios.post(`${base}package/`, formData, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(),
        },
        timeout: AXIOS_TIMEOUT_MS,
      })
    );

    return {
      data: response?.data || null,
      status: response?.status || 201,
    };
  } catch (error) {
    console.error("[FetchCreatePackeges] Error creating package:", error);
    return {
      data: null,
      status: error?.response?.status || 500,
    };
  }
};

export const deletePackeges = async (id) => {
  try {
    const response = await requestWithFallback((base) =>
      axios.delete(`${base}package/${id}`, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        timeout: AXIOS_TIMEOUT_MS,
      })
    );

    return {
      status: response?.status || 200,
    };
  } catch (error) {
    console.error("[deletePackeges] Error deleting package:", error);
    return {
      status: error?.response?.status || 500,
    };
  }
};

export const GetAllPackegesByIdApi = async (id) => {
  try {
    const response = await requestWithFallback((base) =>
      axios.get(`${base}package/${id}`, { timeout: AXIOS_TIMEOUT_MS })
    );

    return {
      data: response?.data || null,
      status: response?.status || 200,
    };
  } catch (error) {
    return {
      data: null,
      status: error?.response?.status || 500,
    };
  }
};

export const UpdatePackegesApi = async (formData, id) => {
  try {
    const response = await requestWithFallback((base) =>
      axios.put(`${base}package/${id}`, formData, {
        headers: {
          ...getAuthHeaders(),
        },
        timeout: AXIOS_TIMEOUT_MS,
      })
    );

    return {
      data: response?.data || null,
      status: response?.status || 200,
    };
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};
